import { useEffect, useMemo, useState } from "react";
import { GeoLocationFilters } from "../components/GeoLocationFilters";
import { PageHeader } from "../components/PageHeader";
import { useAppState } from "../context/AppStateContext";
import type { GeoDirectoryStore } from "../data/geoDirectoryStore";
import { useGeoDirectory } from "../context/GeoDirectoryContext";
import type { AgentAssignedVillage, AgentMaster, AgentMasterUpsertPayload } from "../types";
import { normalizeGeoLabel } from "../utils/geoLabel";
import { toDisplayCamelCase } from "../utils/textCase";

function villageKey(v: AgentAssignedVillage): string {
  return `${v.district}\t${normalizeGeoLabel(v.mandalName)}\t${normalizeGeoLabel(v.villageName)}`;
}

function villageRowKey(v: AgentAssignedVillage, idx: number): string {
  if (v.villageDbId != null && v.villageDbId > 0) {
    return `${v.district}\tvid-${v.villageDbId}`;
  }
  return `${villageKey(v)}\tidx-${idx}`;
}

/** When `embedVillageDbIdInValue` is on, village select value is `"{id}\\t{name}"`. */
function parseVillageSelectValue(raw: string): { villageName: string; villageDbId?: number } {
  const tab = raw.indexOf("\t");
  if (tab > 0) {
    const id = Number(raw.slice(0, tab));
    if (Number.isFinite(id) && id > 0) {
      return { villageName: raw.slice(tab + 1).trim(), villageDbId: id };
    }
  }
  return { villageName: raw.trim() };
}

function enrichAssignedVillagesWithStore(
  villages: AgentAssignedVillage[],
  store: GeoDirectoryStore | null,
): AgentAssignedVillage[] {
  if (!store) return villages;
  return villages.map((v) => {
    if (v.villageDbId != null && v.villageDbId > 0) return v;
    const districtKey = store.findDistrict(v.district)?.districtKey ?? v.district;
    const id = store.tgResolveVillageDbIdInDistrict(districtKey, v.mandalName, v.villageName);
    return id != null ? { ...v, villageDbId: id } : v;
  });
}

type Draft = Omit<AgentMaster, "createdAt">;

function emptyDraft(): Draft {
  return {
    id: "",
    displayName: "",
    phone: "",
    email: "",
    employeeCode: "",
    notes: "",
    hasPassword: false,
    passwordMode: "random",
    onboardingEmailSentAt: null,
    assignedVillages: [],
  };
}

function isDraftNameOk(d: Draft): boolean {
  return Boolean(d.displayName.trim());
}

const MIN_PW = 8;

export function AgentMasterPage() {
  const { agentMasters, addAgentMaster, updateAgentMaster, deleteAgentMaster, sendAgentOnboardingEmail } =
    useAppState();
  const { store } = useGeoDirectory();
  const [mode, setMode] = useState<"list" | "new" | "edit">("list");
  const [draft, setDraft] = useState<Draft>(emptyDraft());
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordFormError, setPasswordFormError] = useState<string | null>(null);
  const [sendingOnboardingId, setSendingOnboardingId] = useState<string | null>(null);
  const [deletingAgentId, setDeletingAgentId] = useState<string | null>(null);
  const [initialPasswordMode, setInitialPasswordMode] = useState<"random" | "custom">("random");
  const [onboardingDialog, setOnboardingDialog] = useState<{ agentId: string; displayName: string } | null>(null);
  const [onboardingDialogPw, setOnboardingDialogPw] = useState("");
  const [onboardingDialogErr, setOnboardingDialogErr] = useState<string | null>(null);
  const [pickDistrict, setPickDistrict] = useState("");
  const [pickMandal, setPickMandal] = useState("");
  const [pickVillage, setPickVillage] = useState("");

  const sorted = useMemo(
    () => [...agentMasters].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [agentMasters],
  );

  useEffect(() => {
    if (mode === "list") {
      setDraft(emptyDraft());
      setNewPassword("");
      setConfirmPassword("");
      setPasswordFormError(null);
      setPickDistrict("");
      setPickMandal("");
      setPickVillage("");
      setOnboardingDialog(null);
      setOnboardingDialogPw("");
      setOnboardingDialogErr(null);
    }
  }, [mode]);

  useEffect(() => {
    if (mode === "list") return;
    if (draft.passwordMode === "random") {
      setNewPassword("");
      setConfirmPassword("");
      setPasswordFormError(null);
    }
  }, [mode, draft.passwordMode]);

  useEffect(() => {
    if (mode !== "edit" && mode !== "new") return;
    if (!store) return;
    setDraft((d) => {
      const next = enrichAssignedVillagesWithStore(d.assignedVillages, store);
      const same = next.length === d.assignedVillages.length && next.every((v, i) => v.villageDbId === d.assignedVillages[i]?.villageDbId);
      return same ? d : { ...d, assignedVillages: next };
    });
  }, [store, mode]);

  useEffect(() => {
    if (!onboardingDialog) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOnboardingDialog(null);
        setOnboardingDialogPw("");
        setOnboardingDialogErr(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onboardingDialog]);

  function startNew() {
    setDraft(emptyDraft());
    setNewPassword("");
    setConfirmPassword("");
    setPasswordFormError(null);
    setInitialPasswordMode("random");
    setMode("new");
  }

  function startEdit(m: AgentMaster) {
    const villagesForEdit = store
      ? enrichAssignedVillagesWithStore([...m.assignedVillages], store)
      : [...m.assignedVillages];
    setDraft({
      id: m.id,
      displayName: m.displayName,
      phone: m.phone,
      email: m.email,
      employeeCode: m.employeeCode,
      notes: m.notes,
      hasPassword: m.hasPassword,
      passwordMode: m.passwordMode ?? "random",
      onboardingEmailSentAt: m.onboardingEmailSentAt,
      assignedVillages: villagesForEdit,
    });
    setNewPassword("");
    setConfirmPassword("");
    setPasswordFormError(null);
    setInitialPasswordMode(m.passwordMode ?? "random");
    setPickDistrict("");
    setPickMandal("");
    setPickVillage("");
    setMode("edit");
  }

  function addPickedVillage() {
    if (!pickDistrict || !pickMandal.trim() || !pickVillage.trim()) return;
    const parsed = parseVillageSelectValue(pickVillage);
    if (!parsed.villageName) return;
    const resolvedId =
      parsed.villageDbId ??
      (store ? store.tgResolveVillageDbIdInDistrict(pickDistrict, pickMandal, parsed.villageName) : undefined);
    const next: AgentAssignedVillage = {
      district: pickDistrict,
      mandalName: pickMandal.trim(),
      villageName: parsed.villageName,
      ...(resolvedId != null ? { villageDbId: resolvedId } : {}),
    };
    const dedupeKey =
      next.villageDbId != null && next.villageDbId > 0 ? `vid:${next.villageDbId}` : villageKey(next);
    if (draft.assignedVillages.some((v) => (v.villageDbId != null && v.villageDbId > 0 ? `vid:${v.villageDbId}` : villageKey(v)) === dedupeKey)) {
      return;
    }
    setDraft((d) => ({ ...d, assignedVillages: [...d.assignedVillages, next] }));
    setPickVillage("");
  }

  function removeVillage(idx: number) {
    setDraft((d) => ({
      ...d,
      assignedVillages: d.assignedVillages.filter((_, i) => i !== idx),
    }));
  }

  function buildUpsertPayload(): AgentMasterUpsertPayload | null {
    const np = newPassword.trim();
    const cp = confirmPassword.trim();
    const row: AgentMasterUpsertPayload = {
      displayName: draft.displayName.trim(),
      phone: draft.phone.trim(),
      email: draft.email.trim(),
      employeeCode: draft.employeeCode.trim(),
      notes: draft.notes.trim(),
      assignedVillages: draft.assignedVillages,
      passwordMode: draft.passwordMode,
    };

    if (draft.passwordMode === "custom") {
      const mustSetPassword =
        mode === "new" ||
        !draft.hasPassword ||
        (initialPasswordMode === "random" && draft.passwordMode === "custom");
      if (mustSetPassword) {
        if (np.length < MIN_PW) {
          setPasswordFormError(`Password must be at least ${MIN_PW} characters.`);
          return null;
        }
        if (np !== cp) {
          setPasswordFormError("Password and confirmation do not match.");
          return null;
        }
        row.newPassword = np;
      } else if (np || cp) {
        if (np.length < MIN_PW) {
          setPasswordFormError(`Password must be at least ${MIN_PW} characters.`);
          return null;
        }
        if (np !== cp) {
          setPasswordFormError("Password and confirmation do not match.");
          return null;
        }
        row.newPassword = np;
      }
    }

    setPasswordFormError(null);
    return row;
  }

  async function saveAgent() {
    if (!isDraftNameOk(draft)) return;
    const row = buildUpsertPayload();
    if (!row) return;
    if (mode === "new") {
      const id = await addAgentMaster(row);
      if (!id) return;
    } else if (mode === "edit" && draft.id) {
      const ok = await updateAgentMaster(draft.id, row);
      if (!ok) return;
    }
    setMode("list");
  }

  async function handleSendOnboarding(agentId: string) {
    const agent = agentMasters.find((a) => a.id === agentId);
    if (!agent) return;
    if (agent.passwordMode === "custom" && agent.hasPassword) {
      setOnboardingDialog({ agentId, displayName: agent.displayName });
      setOnboardingDialogPw("");
      setOnboardingDialogErr(null);
      return;
    }
    setSendingOnboardingId(agentId);
    try {
      await sendAgentOnboardingEmail(agentId);
    } finally {
      setSendingOnboardingId(null);
    }
  }

  async function handleDeleteAgent(m: AgentMaster) {
    const ok = window.confirm(
      `Remove agent “${m.displayName}” from the master list?\n\nAssigned villages will be cleared. This cannot be undone.`,
    );
    if (!ok) return;
    setDeletingAgentId(m.id);
    try {
      await deleteAgentMaster(m.id);
    } finally {
      setDeletingAgentId(null);
    }
  }

  async function submitOnboardingDialog() {
    if (!onboardingDialog) return;
    const pw = onboardingDialogPw.trim();
    if (!pw) {
      setOnboardingDialogErr("Enter the agent password.");
      return;
    }
    setOnboardingDialogErr(null);
    setSendingOnboardingId(onboardingDialog.agentId);
    try {
      const ok = await sendAgentOnboardingEmail(onboardingDialog.agentId, { customPasswordForEmail: pw });
      if (ok) {
        setOnboardingDialog(null);
        setOnboardingDialogPw("");
      }
    } finally {
      setSendingOnboardingId(null);
    }
  }

  const canSave = isDraftNameOk(draft);

  return (
    <>
      <PageHeader
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Agent master" }]}
        title="Agent master"
      />

      <p className="section-lead section-lead--tight text-muted" style={{ marginBottom: "0.75rem" }}>
        Register field agents and assign villages from the geographic directory. Villages are matched the same way as
        on the Applicants screen.
      </p>

      {mode === "list" ? (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "0.75rem",
            }}
          >
            <button type="button" className="btn btn--primary btn--tg" onClick={startNew}>
              Add agent
            </button>
          </div>
          <div className="card card--gov card--elevated">
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Employee ref.</th>
                    <th>Villages</th>
                    <th>Sign-in</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {sorted.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="data-table__empty">
                        No agents yet. Use Add agent to create one.
                      </td>
                    </tr>
                  ) : (
                    sorted.map((m) => (
                      <tr key={m.id}>
                        <td>
                          <span className="table-name">{m.displayName}</span>
                        </td>
                        <td className="mono">{m.phone || "—"}</td>
                        <td>{m.email || "—"}</td>
                        <td className="mono">{m.employeeCode || "—"}</td>
                        <td>
                          {m.assignedVillages.length === 0 ? (
                            <span className="text-muted">None</span>
                          ) : (
                            <span>{m.assignedVillages.length} village(s)</span>
                          )}
                        </td>
                        <td>
                          <div className="agent-master-form__stack">
                            <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                              {m.passwordMode === "custom" ? "Custom" : "Random"}
                              {" · "}
                              {m.hasPassword ? "Password set" : "No password"}
                            </span>
                            {m.onboardingEmailSentAt ? (
                              <span className="text-muted" style={{ fontSize: "0.8rem" }}>
                                Emailed {new Date(m.onboardingEmailSentAt).toLocaleDateString()}
                              </span>
                            ) : null}
                            <button
                              type="button"
                              className="btn btn--sm btn--secondary btn--tg"
                              disabled={
                                Boolean(sendingOnboardingId) ||
                                !m.email.trim() ||
                                (m.passwordMode === "custom" && !m.hasPassword)
                              }
                              title={
                                !m.email.trim()
                                  ? "Add an email to send onboarding"
                                  : m.passwordMode === "custom" && !m.hasPassword
                                    ? "Save a custom password for this agent first"
                                    : m.passwordMode === "custom"
                                      ? "Opens a short step: enter the saved password to verify it matches and include it in the email"
                                      : "Creates a new random password and emails it with the sign-in link"
                              }
                              onClick={() => void handleSendOnboarding(m.id)}
                            >
                              {sendingOnboardingId === m.id ? "Sending…" : "Send onboarding"}
                            </button>
                          </div>
                        </td>
                        <td>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem", justifyContent: "flex-end" }}>
                            <button type="button" className="btn btn--sm btn--tg-secondary" onClick={() => startEdit(m)}>
                              Edit
                            </button>
                            <button
                              type="button"
                              className="btn btn--sm btn--danger"
                              disabled={deletingAgentId !== null || sendingOnboardingId === m.id}
                              onClick={() => void handleDeleteAgent(m)}
                            >
                              {deletingAgentId === m.id ? "Removing…" : "Delete"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="card card--gov card--elevated agent-master-form">
          <h2 className="card__title card__title--gov">{mode === "new" ? "New agent" : "Edit agent"}</h2>

          <h3 className="form-section-title">Contact &amp; details</h3>
          <div className="field-grid">
            <div className="field">
              <label htmlFor="agent-display-name">Display name</label>
              <input
                id="agent-display-name"
                value={draft.displayName}
                onChange={(e) => setDraft((d) => ({ ...d, displayName: e.target.value }))}
                autoComplete="name"
              />
            </div>
            <div className="field">
              <label htmlFor="agent-phone">Phone</label>
              <input
                id="agent-phone"
                value={draft.phone}
                onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))}
                autoComplete="tel"
              />
            </div>
            <div className="field">
              <label htmlFor="agent-email">Email</label>
              <input
                id="agent-email"
                type="email"
                value={draft.email}
                onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
                autoComplete="email"
              />
            </div>
            <div className="field">
              <label htmlFor="agent-emp">Employee / ID ref.</label>
              <input
                id="agent-emp"
                value={draft.employeeCode}
                onChange={(e) => setDraft((d) => ({ ...d, employeeCode: e.target.value }))}
              />
            </div>
            <div className="field field--full">
              <label htmlFor="agent-notes">Notes</label>
              <textarea
                id="agent-notes"
                rows={4}
                value={draft.notes}
                onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
              />
            </div>
          </div>

          <h3 className="form-section-title">Sign-in (email + password)</h3>
          <p className="agent-master-form__hint">
            Agents use the <strong>email</strong> above to sign in. Choose <strong>Random</strong> to let the system
            generate a password when you send onboarding, or <strong>Custom</strong> to set a password here; for custom,
            you re-enter that password when sending onboarding so it can be verified and included in the message (the
            password is stored on the server as entered).
          </p>
          {mode === "edit" ? (
            <p className="agent-master-form__hint" style={{ marginTop: "-0.25rem" }}>
              Status:{" "}
              <strong>{draft.hasPassword ? "Password is set" : "No password yet"}</strong>
              {draft.onboardingEmailSentAt
                ? ` · Onboarding email last sent ${new Date(draft.onboardingEmailSentAt).toLocaleString()}`
                : ""}
            </p>
          ) : null}
          <div className="field field--full" style={{ marginBottom: "0.5rem" }}>
            <span className="text-muted" style={{ fontSize: "0.85rem", display: "block", marginBottom: "0.35rem" }}>
              Password type
            </span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
              <label style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", cursor: "pointer" }}>
                <input
                  type="radio"
                  name="agent-pw-mode"
                  checked={draft.passwordMode === "random"}
                  onChange={() => setDraft((d) => ({ ...d, passwordMode: "random" }))}
                />
                Random
              </label>
              <label style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", cursor: "pointer" }}>
                <input
                  type="radio"
                  name="agent-pw-mode"
                  checked={draft.passwordMode === "custom"}
                  onChange={() => setDraft((d) => ({ ...d, passwordMode: "custom" }))}
                />
                Custom
              </label>
            </div>
          </div>
          {draft.passwordMode === "random" ? (
            <p className="agent-master-form__hint" style={{ marginTop: 0 }}>
              No password is stored until you use <strong>Send onboarding</strong> from the list; each send creates a
              new random password and emails it.
            </p>
          ) : (
            <div className="field-grid">
              <div className="field">
                <label htmlFor="agent-new-pw">Password</label>
                <input
                  id="agent-new-pw"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setPasswordFormError(null);
                  }}
                  placeholder={
                    mode === "edit" && draft.hasPassword && !(initialPasswordMode === "random")
                      ? "Leave blank to keep current"
                      : "At least 8 characters"
                  }
                />
              </div>
              <div className="field">
                <label htmlFor="agent-confirm-pw">Confirm password</label>
                <input
                  id="agent-confirm-pw"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setPasswordFormError(null);
                  }}
                  placeholder="Repeat password"
                />
              </div>
            </div>
          )}
          {passwordFormError ? (
            <p className="agent-master-form__hint" style={{ color: "var(--danger, #b91c1c)" }}>
              {passwordFormError}
            </p>
          ) : null}

          <h3 className="form-section-title">Assign villages</h3>
          <p className="agent-master-form__hint">
            Pick <strong>district</strong>, <strong>mandal</strong>, and <strong>village</strong>, then use{" "}
            <strong>Add village</strong>. The database keys each assignment by <strong>Geo ID</strong> (with district /
            mandal / village names stored for display). The server rejects saves that cannot be matched to the geo
            directory.
          </p>
          <div className="agent-master-form__geo" aria-label="Pick village to assign">
            <GeoLocationFilters
              idPrefix="agent-master-geo"
              district={pickDistrict}
              mandal={pickMandal}
              village={pickVillage}
              embedVillageDbIdInValue
              onDistrictChange={(d) => {
                setPickDistrict(d);
                setPickMandal("");
                setPickVillage("");
              }}
              onMandalChange={(m) => {
                setPickMandal(m);
                setPickVillage("");
              }}
              onVillageChange={setPickVillage}
            />
            <div className="agent-master-form__geo-btn">
              <button
                type="button"
                className="btn btn--secondary btn--tg"
                aria-label="Add selected village to this agent"
                onClick={addPickedVillage}
              >
                Add village
              </button>
            </div>
          </div>

          <div className="agent-master-form__village-table">
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>District</th>
                    <th className="mono">Reg. code</th>
                    <th>District (official)</th>
                    <th className="mono">Mandal ID</th>
                    <th className="mono">Mandal code</th>
                    <th>Mandal</th>
                    <th className="mono">Village code</th>
                    <th>Village</th>
                    <th className="mono">Geo ID</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {draft.assignedVillages.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="data-table__empty">
                        No villages assigned yet.
                      </td>
                    </tr>
                  ) : (
                    draft.assignedVillages.map((v, idx) => (
                      <tr key={villageRowKey(v, idx)}>
                        <td>
                          {v.registrationDistrictName || toDisplayCamelCase(v.district) || "—"}
                        </td>
                        <td className="mono text-muted">{v.registrationDistrictCode || "—"}</td>
                        <td>{v.registrationDistrictName || "—"}</td>
                        <td className="mono text-muted">{v.mandalId != null ? String(v.mandalId) : "—"}</td>
                        <td className="mono text-muted">{v.mandalCode || "—"}</td>
                        <td>{v.mandalName}</td>
                        <td className="mono text-muted">{v.villageCode || "—"}</td>
                        <td>{v.villageName}</td>
                        <td className="mono text-muted">{v.villageDbId != null ? String(v.villageDbId) : "—"}</td>
                        <td>
                          <button type="button" className="btn btn--link btn--sm" onClick={() => removeVillage(idx)}>
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="agent-master-form__actions">
            <button type="button" className="btn btn--primary btn--tg" disabled={!canSave} onClick={() => void saveAgent()}>
              Save
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => setMode("list")}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {onboardingDialog ? (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => {
            setOnboardingDialog(null);
            setOnboardingDialogPw("");
            setOnboardingDialogErr(null);
          }}
        >
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="agent-onboard-dialog-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="agent-onboard-dialog-title">Send onboarding email</h2>
            <p className="modal__lede">
              <span className="table-name">{onboardingDialog.displayName}</span>
              <span className="text-muted" style={{ display: "block", marginTop: "0.35rem" }}>
                Custom sign-in: enter the same password you saved for this agent. It is checked against the saved
                password and included in the email; the database row is not updated by this send.
              </span>
            </p>
            <div className="field" style={{ marginTop: "0.75rem" }}>
              <label htmlFor="agent-onboard-pw">Agent password</label>
              <input
                id="agent-onboard-pw"
                type="password"
                autoComplete="off"
                value={onboardingDialogPw}
                onChange={(e) => {
                  setOnboardingDialogPw(e.target.value);
                  setOnboardingDialogErr(null);
                }}
              />
            </div>
            {onboardingDialogErr ? (
              <p className="agent-master-form__hint" style={{ color: "var(--danger, #b91c1c)", marginTop: "0.5rem" }}>
                {onboardingDialogErr}
              </p>
            ) : null}
            <div className="modal__actions" style={{ marginTop: "1rem" }}>
              <button
                type="button"
                className="btn btn--primary btn--tg"
                disabled={Boolean(sendingOnboardingId)}
                onClick={() => void submitOnboardingDialog()}
              >
                {sendingOnboardingId === onboardingDialog.agentId ? "Sending…" : "Send email"}
              </button>
              <button
                type="button"
                className="btn btn--ghost"
                disabled={Boolean(sendingOnboardingId)}
                onClick={() => {
                  setOnboardingDialog(null);
                  setOnboardingDialogPw("");
                  setOnboardingDialogErr(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
